import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { environment } from '@env/environment';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { sumBy, orderBy, findIndex, find, cloneDeep } from 'lodash';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { Subject } from 'rxjs';
import { ViewMunicipalitiesLumenPerWattRanking } from '@app/shared/models/energiekarte/ViewMunicipalitiesLumenPerWattRanking';

@Component({
  selector: 'app-streetlight-efficiency-dialog',
  templateUrl: './streetlight-efficiency-dialog.component.html',
  styleUrls: ['./streetlight-efficiency-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [LocalNumberPipe],
})
export class StreetlightEfficiencyDialogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  efficiency?: ViewMunicipalitiesLumenPerWattRanking = {} as ViewMunicipalitiesLumenPerWattRanking;
  range = { minValue: 0, maxValue: 150 };
  chartData: any[] = [];
  marker = `./assets/icons/arrow-${environment.tenant.trim().toLowerCase()}.svg`;
  averageEfficiency = 0;
  isLoading = false;
  originalRanking: ViewMunicipalitiesLumenPerWattRanking[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private municipalitiesService: MunicipalityService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    this.isLoading = true;
    let municipality: MapStoreMunicipality;
    let municipalityKey: string;
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((currentMunicipality: MapStoreMunicipality) => {
          municipality = currentMunicipality;
          municipalityKey = currentMunicipality.key;
          return this.municipalitiesService.getLumenPerWattRangking();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((efficiencies: ViewMunicipalitiesLumenPerWattRanking[]) => {
        this.originalRanking = cloneDeep(efficiencies);

        efficiencies = efficiencies.map((a: ViewMunicipalitiesLumenPerWattRanking) => {
          return {
            guid: a.guid,
            municipalityKey: a.municipalityKey,
            name: a.name,
            lumenPerWattCurrent: a.lumenPerWattCurrent,
            lumenPerWattOptimized: a.municipalityKey === municipality.key ? a.lumenPerWattOptimized : a.lumenPerWattCurrent,
          } as ViewMunicipalitiesLumenPerWattRanking;
        });

        this.applyCustomRanking(efficiencies, 'lumenPerWattCurrent', 'currentRank');
        this.applyCustomRanking(efficiencies, 'lumenPerWattOptimized', 'optimizedRank');
        this.efficiency = find(efficiencies, (a: ViewMunicipalitiesLumenPerWattRanking) => a.municipalityKey === municipalityKey);
        this.averageEfficiency = sumBy(efficiencies, 'lumenPerWattCurrent') / efficiencies.length;
        this.isLoading = false;

        this.chartData = [
          {
            indicator: 'companyColor',
            value: this.efficiency ? this.efficiency.lumenPerWattOptimized : 0,
            unit: 'lm/W',
            ranking: this.efficiency ? this.efficiency.optimizedRank : 0,
            municipalities: efficiencies.length,
          },
          {
            indicator: 'lightGrey',
            value: this.efficiency ? this.efficiency.lumenPerWattCurrent : 0,
            unit: 'lm/W',
            ranking: this.efficiency ? this.efficiency.currentRank : 0,
            municipalities: efficiencies.length,
          },
          {
            indicator: 'darkGrey',
            value: this.averageEfficiency,
            unit: 'lm/W',
            ranking: null,
            municipalities: null,
          },
        ];

        if (this.data.isPlanner) {
          this.applyPlannerRanking(efficiencies, municipality);
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyPlannerRanking(efficiencies: ViewMunicipalitiesLumenPerWattRanking[], municipality: MapStoreMunicipality): void {
    efficiencies.forEach((a: any) => {
      a.lumenPerWattPlanner = a.municipalityKey === municipality.key ? this.data.newEfficiency : a.lumenPerWattCurrent;
      return a;
    });

    this.applyCustomRanking(efficiencies, 'lumenPerWattPlanner', 'plannerRank');

    const municipalityRank = find(efficiencies, (a: ViewMunicipalitiesLumenPerWattRanking) => a.municipalityKey === municipality.key);

    this.chartData.push({
      indicator: 'green',
      value: this.data.newEfficiency,
      unit: 'lm/W',
      ranking: municipalityRank?.plannerRank,
      municipalities: efficiencies.length,
    });
  }

  private applyCustomRanking(efficiencies: ViewMunicipalitiesLumenPerWattRanking[], sortColumn: string, rankProperty: string): void {
    const sorted = orderBy(efficiencies, [sortColumn], ['desc']);
    efficiencies.forEach((a: ViewMunicipalitiesLumenPerWattRanking) => {
      a[rankProperty] = findIndex(sorted, (b) => b[sortColumn] === a[sortColumn]) + 1;
      return a;
    });
  }
}
